import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Modal } from '@bruno/ui';
import {
  adminImpersonate,
  adminListPlans,
  adminListUserSubscriptions,
  adminListUsers,
  adminSetUserSubscriptionPlan,
  adminSuspendUser,
  adminUnsuspendUser,
} from '../api/admin';
import { API_URL } from '../config';

function dollars(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

interface ManageTarget {
  id: string;
  email: string;
}

export function UsersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [manageTarget, setManageTarget] = useState<ManageTarget | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => adminListUsers(search || undefined),
  });

  const suspend = useMutation({
    mutationFn: adminSuspendUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
  const unsuspend = useMutation({
    mutationFn: adminUnsuspendUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  async function handleImpersonate(id: string, email: string) {
    if (!confirm(`Impersonate ${email}? This will be recorded in the audit log.`)) return;
    const { accessToken } = await adminImpersonate(id);
    const dashboardUrl = (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ?? '';
    if (dashboardUrl) {
      const u = new URL(dashboardUrl);
      u.searchParams.set('impersonate_token', accessToken);
      window.open(u.toString(), '_blank', 'noopener');
    } else {
      window.prompt(
        `API: ${API_URL}\nAccess token (paste into customer dashboard storage):`,
        accessToken,
      );
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Users</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(q);
        }}
        className="flex gap-2 items-end"
      >
        <Input
          label="Search by email or name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      {isLoading ? (
        <div className="text-slate-500">Loading...</div>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left">Role</th>
                  <th className="text-left">Websites</th>
                  <th className="text-left">Balance</th>
                  <th className="text-left">Created</th>
                  <th className="text-right px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.users ?? []).map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2">
                      <div className="font-medium">{u.email}</div>
                      {u.name ? <div className="text-xs text-slate-500">{u.name}</div> : null}
                    </td>
                    <td>
                      <Badge tone={u.role === 'admin' ? 'indigo' : 'gray'}>{u.role}</Badge>
                    </td>
                    <td>{u.websiteCount}</td>
                    <td>{dollars(u.balanceCents)}</td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setManageTarget({ id: u.id, email: u.email })}
                      >
                        Manage plans
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleImpersonate(u.id, u.email)}
                      >
                        Impersonate
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => suspend.mutate(u.id)}
                      >
                        Suspend
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => unsuspend.mutate(u.id)}
                      >
                        Unsuspend
                      </Button>
                    </td>
                  </tr>
                ))}
                {(data?.users ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-slate-500 py-6">
                      No users found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ManagePlansModal
        target={manageTarget}
        onClose={() => setManageTarget(null)}
      />
    </div>
  );
}

function ManagePlansModal({
  target,
  onClose,
}: {
  target: ManageTarget | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = target !== null;
  const userId = target?.id ?? '';

  const subs = useQuery({
    queryKey: ['admin-user-subs', userId],
    queryFn: () => adminListUserSubscriptions(userId),
    enabled: open,
  });
  const allPlans = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminListPlans,
    enabled: open,
  });

  // Per-row local choice of which plan to assign. Keyed by websiteId so
  // multiple sites in the modal don't share one selector.
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setSelections({});
      setErrors({});
    }
  }, [open]);

  const setPlan = useMutation({
    mutationFn: ({ websiteId, planId }: { websiteId: string; planId: string }) =>
      adminSetUserSubscriptionPlan(userId, websiteId, planId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin-user-subs', userId] });
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      setErrors((prev) => {
        const next = { ...prev };
        delete next[vars.websiteId];
        return next;
      });
    },
    onError: (err: Error, vars) => {
      setErrors((prev) => ({ ...prev, [vars.websiteId]: err.message }));
    },
  });

  function handleApply(websiteId: string, currentPlanId: string | null) {
    const planId = selections[websiteId] ?? currentPlanId ?? '';
    if (!planId) return;
    if (
      !confirm(
        'This will cancel the current Stripe subscription for this site (without refund) and assign the selected plan locally. The user can still pick another plan later via the dashboard. Continue?',
      )
    ) {
      return;
    }
    setPlan.mutate({ websiteId, planId });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={target ? `Manage plans for ${target.email}` : 'Manage plans'}
      className="max-w-2xl"
    >
      {!open ? null : subs.isLoading || allPlans.isLoading ? (
        <div className="text-slate-500 text-sm">Loading...</div>
      ) : (
        <div className="flex flex-col gap-3">
          {(subs.data?.items ?? []).length === 0 ? (
            <div className="text-sm text-slate-500 italic">
              This user has no websites yet.
            </div>
          ) : (
            (subs.data?.items ?? []).map((row) => {
              const plansList = (allPlans.data?.plans ?? []).filter((p) => !p.archived);
              const currentPlanId = row.plan?.id ?? null;
              const selected =
                selections[row.websiteId] ?? currentPlanId ?? plansList[0]?.id ?? '';
              const isSubmitting =
                setPlan.isPending && setPlan.variables?.websiteId === row.websiteId;
              const hasStripe = Boolean(row.subscription?.stripeSubscriptionId);
              const errorMsg = errors[row.websiteId];
              return (
                <div
                  key={row.websiteId}
                  className="rounded-lg border border-slate-200 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{row.websiteName}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {row.websiteDomain}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        <span className="text-slate-500">Current:</span>
                        <span className="font-medium">{row.plan?.name ?? '—'}</span>
                        {row.subscription ? (
                          <Badge
                            tone={
                              row.subscription.status === 'active' ||
                              row.subscription.status === 'trialing'
                                ? 'green'
                                : row.subscription.status === 'past_due'
                                ? 'amber'
                                : 'red'
                            }
                          >
                            {row.subscription.status}
                          </Badge>
                        ) : null}
                        {hasStripe ? <Badge tone="indigo">stripe</Badge> : null}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <select
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                        value={selected}
                        onChange={(e) =>
                          setSelections((prev) => ({
                            ...prev,
                            [row.websiteId]: e.target.value,
                          }))
                        }
                      >
                        {plansList.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} (${(p.monthlyPriceCents / 100).toFixed(2)}/mo)
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        onClick={() => handleApply(row.websiteId, currentPlanId)}
                        disabled={!selected || selected === currentPlanId}
                        loading={isSubmitting}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                  {errorMsg ? (
                    <div className="mt-2 text-xs text-red-600">{errorMsg}</div>
                  ) : null}
                </div>
              );
            })
          )}
          <p className="mt-2 text-xs text-slate-500">
            Applying a plan cancels any active Stripe subscription for that site
            without a refund and switches the local subscription to the chosen
            plan. The user can still pick a different plan via their dashboard
            afterwards (Stripe checkout).
          </p>
          <div className="flex justify-end pt-1">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
